import { useNavigate } from "react-router-dom";
import { ArrowLeft, LayoutGrid, List, Trash2 } from "lucide-react";

interface ControlsBarProps {
  viewMode: "list" | "grid";
  onViewModeChange: (mode: "list" | "grid") => void;
  showDelete: boolean;
  onDelete: () => void;
}

const SECTION_ANCHORS = [
  { label: "Distribution", id: "section-distribution" },
  { divider: true },
  { label: "Identity", id: "section-identity" },
  { label: "Prompts", id: "section-prompts" },
  { label: "Insights", id: "section-insights" },
  { label: "Knowledge", id: "section-knowledge" },
  { divider: true },
  { label: "Branding", id: "section-branding" },
  { label: "Messaging", id: "section-mailbox" },
  { divider: true },
  { label: "Tools", id: "section-tools" },
  { label: "AI Config", id: "section-intelligence" },
  { label: "Extra", id: "section-extra" },
];

export const ControlsBar = ({ viewMode, onViewModeChange, showDelete, onDelete }: ControlsBarProps) => {
  const navigate = useNavigate();

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    const container = document.getElementById("main-scroll-container");
    if (!el || !container) return;
    const offset = 64;
    const top = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - offset;
    container.scrollTo({ top, behavior: "smooth" });
  };

  return (
    <div className="sticky top-0 z-20 flex flex-nowrap items-center gap-1 mb-6 bg-[#F9FAFC] py-3 -mx-4 px-4 border-b border-gray-200 overflow-x-auto scrollbar-hide">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-gray-600 hover:text-gray-900 transition-colors shrink-0"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm font-medium">Back</span>
      </button>

      <div className="w-px h-5 bg-gray-200 mx-0.5 shrink-0 hidden md:block" />

      <div className="hidden md:flex items-center bg-white border border-gray-200 rounded-lg p-0.5 shrink-0">
        <button
          onClick={() => onViewModeChange("list")}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === "list" ? "bg-gray-100 text-gray-900" : "text-gray-500 hover:text-gray-700"}`}
        >
          <List className="w-3.5 h-3.5" />
          List
        </button>
        <button
          onClick={() => onViewModeChange("grid")}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === "grid" ? "bg-gray-100 text-gray-900" : "text-gray-500 hover:text-gray-700"}`}
        >
          <LayoutGrid className="w-3.5 h-3.5" />
          Grid
        </button>
      </div>

      <div className="w-px h-5 bg-gray-200 mx-0.5 shrink-0 hidden md:block" />

      {SECTION_ANCHORS.map((item, i) =>
        "divider" in item ? (
          <div key={i} className="w-px h-5 bg-gray-200 mx-0.5 shrink-0 hidden md:block" />
        ) : (
          <button
            key={item.id}
            onClick={() => scrollToSection(item.id)}
            className="hidden md:block px-2.5 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-white border border-transparent hover:border-gray-200 rounded-lg transition-all shrink-0"
          >
            {item.label}
          </button>
        ),
      )}

      {showDelete && (
        <>
          <div className="w-px h-5 bg-gray-200 mx-0.5 shrink-0" />
          <button
            onClick={onDelete}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 border border-transparent hover:border-red-200 rounded-lg transition-all shrink-0"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete
          </button>
        </>
      )}
    </div>
  );
};
